import { Box } from '../primitives';
import { palette } from '../theme/palette';
import { usePortalPalette } from '../theme/usePortalPalette';

/** The id every app shell puts on its `<main>`, and the skip link's target. */
export const MAIN_CONTENT_ID = 'main-content';

export interface SkipLinkProps {
  /** Localized label, e.g. `t('common.a11y.skipToContent')`. */
  label: string;
  /** Target element id. Defaults to the shared `main-content`. */
  targetId?: string;
}

/**
 * "Skip to main content" — WCAG 2.4.1 (Bypass Blocks).
 *
 * Every shell in this repo puts a long navigation region ahead of the page
 * content: the gov rail (5 links + sign-out + collapse + language + account),
 * the admin sidebar (40+ links) and the marketing header. Without this, a
 * keyboard or switch user re-traverses all of it on every single route.
 *
 * Rendered as the first focusable node in the shell and hidden off-screen until
 * focused, so it costs sighted mouse users nothing. Activating it moves focus —
 * not just scroll — to `<main>`, which is why the shells also give `<main>` a
 * `tabIndex={-1}`.
 */
export function SkipLink({ label, targetId = MAIN_CONTENT_ID }: SkipLinkProps) {
  const portal = usePortalPalette();
  return (
    <Box
      component="a"
      href={`#${targetId}`}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        // Anchor navigation alone scrolls without moving focus in every major
        // browser, so drive focus explicitly.
        event.preventDefault();
        const target = document.getElementById(targetId);
        if (!target) return;
        target.focus();
        target.scrollIntoView({ block: 'start' });
      }}
      sx={{
        position: 'fixed',
        top: 8,
        left: 8,
        zIndex: 2000,
        // Off-screen rather than `display: none`, so it stays in the tab order.
        transform: 'translateY(-200%)',
        px: 2,
        py: 1.25,
        borderRadius: 1,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
        color: palette.text.onDark,
        backgroundColor: portal.brand.rail,
        border: `1px solid ${palette.border.focusOnDark}`,
        transition: 'transform 200ms ease-out',
        '&:focus-visible, &:focus': { transform: 'translateY(0)' },
      }}
    >
      {label}
    </Box>
  );
}
